import { AttributeTypeModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTablePresentationProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VariantRow from "@SupplyLand/Widgets/ProductList/VariantRow";
import cloneDeep from "lodash/cloneDeep";
import React from "react";
import { css } from "styled-components";

export interface VariantTableStyles {
    variantTable?: DataTablePresentationProps;
    kraftBoxesTable?: DataTablePresentationProps;
    tableContainer?: InjectableCss;
    priceCell?: InjectableCss;
    header?: InjectableCss;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToCartButton?: InjectableCss;
    quantityOrdered?: TextFieldProps;
    variantTableRow?: DataTableRowProps;
    basicVariantTableRow?: DataTableRowProps;
    leftTableContent?: InjectableCss;
    rightTableContent?: InjectableCss;
    addToCartColumn?: InjectableCss;
}

export const variantTableStyles: VariantTableStyles = {
    variantTable: {
        css: css`
            width: 100%;
            td {
                border: 0.5px solid black;
                text-align: center;
                padding: 0px 8px;
            }
            th {
                border: 0.5px solid black;
                text-align: center;
                max-width: 130px;
                white-space: normal;
                background: rgb(251 241 200);
                padding: 0px 8px;
            }
        `,
    },
    kraftBoxesTable: {
        css: css`
            width: 100%;
            td {
                border: 0.5px solid black;
                text-align: center;
                height: 35px;
                padding: 0px 8px;
            }
            th {
                border: 0.5px solid black;
                text-align: center;
                white-space: normal;
                background: rgb(251 241 200);
                padding: 0px 8px;
            }
            input {
                height: 25px;
            }
            button {
                min-height: 25px;
            }
        `,
    },
    priceCell: {
        css: css`
            border-left: none !important;
            border-right: none !important;
            background: none;
        `,
    },
    header: {
        css: css`
            border-bottom: 0px solid !important;
        `,
    },
};

const styles = variantTableStyles;

interface OwnProps {
    product: ProductModel;
    parentProduct: ProductModel;
    variantChildren: ProductModel[];
    priceHeaders: string[];
    isParentChildConnector: boolean;
}

const VariantTable: React.FC<OwnProps> = props => {
    const variantChildren = props.variantChildren;
    const priceHeaders = props.priceHeaders;
    const parentProduct = props.parentProduct;
    const priceHeaderRowSpan = priceHeaders.length === 1 ? 1 : 2;
    let priceLoaded = false;

    const getVariantHeaders = (styleValues: string[]) => {
        if (styleValues.length === 0) {
            return null;
        }
        styleValues = styleValues.filter(variant => variant !== "Parent Child Connector");
        return styleValues.map((header, index) => (
            <DataTableHeader rowSpan={priceHeaderRowSpan} key={header + index}>
                {header}
            </DataTableHeader>
        ));
    };

    const getAttributeHeaders = (attributeNames: string[], firstChild: ProductModel) => {
        if (attributeNames.length === 0 || attributeNames[0] === "") {
            return null;
        }

        const attributeHeaders = [] as string[];

        attributeNames.forEach(attributeName => {
            const attributeColumn = firstChild.attributeTypes?.find(
                (attribute: AttributeTypeModel) => attribute.label === attributeName,
            );
            if (attributeColumn) {
                attributeHeaders.push(attributeColumn.label);
            } else {
                return null;
            }
        });

        return attributeHeaders.map(header => {
            const headerWithSplitChar = header.replace("(", ",(");
            const headerArray = headerWithSplitChar.split(",");
            if (headerArray.length > 1) {
                return (
                    <DataTableHeader rowSpan={priceHeaderRowSpan} key={header}>
                        {headerArray[0]} <br></br> {headerArray[1]}
                    </DataTableHeader>
                );
            }
            return (
                <DataTableHeader rowSpan={priceHeaderRowSpan} key={header}>
                    {header}
                </DataTableHeader>
            );
        });
    };

    const getTopPriceHeader = (breakPrices: string[]) => {
        if (breakPrices.length === 0 || breakPrices.length === 1) {
            <DataTableHeader>
                <></>
            </DataTableHeader>;
        }

        const title = parentProduct.properties["table_Price_Break_UoM"];
        let priceTitleDisplay = "Price Per Item";

        if (title) {
            priceTitleDisplay = title;
        }

        return <DataTableHeader colSpan={breakPrices.length}>{priceTitleDisplay}</DataTableHeader>;
    };

    const getPriceHeaders = (breakPrices: string[], productId: string) => {
        if (breakPrices.length === 0) {
            return null;
        }

        let lastBreak = breakPrices.pop();
        if (lastBreak) {
            lastBreak += "+";
            breakPrices.push(lastBreak);
        }

        priceLoaded = true;
        if (
            parentProduct.productNumber === "KraftBoxes" ||
            parentProduct.productNumber === "MultiDepthBoxes" ||
            parentProduct.productNumber === "TallKraftBoxes"
        ) {
            breakPrices[0] = "25";
        }
        return breakPrices.map((breakQty, index) => (
            <DataTableHeader {...styles.priceCell} key={breakQty + productId + index}>
                {breakQty}
            </DataTableHeader>
        ));
    };

    // Variant Headers
    const variantHeaders: string[] = [];
    if (parentProduct.variantTraits !== undefined) {
        const traits = cloneDeep(parentProduct.variantTraits.slice());
        const sortedTraits = traits.sort((a, b) => a.sortOrder - b.sortOrder);
        sortedTraits.forEach(style => {
            variantHeaders.push(style.nameDisplay);
        });
    }

    // Attribute Headers
    let attributeNames: string[] = [];
    if (parentProduct.properties && parentProduct.properties["displayedAttributes"] != null) {
        attributeNames = parentProduct.properties["displayedAttributes"].split(",");
        const trimmedNames: string[] = [];
        attributeNames.forEach(name => {
            const trimmedName = name.trim();
            trimmedNames.push(trimmedName);
        });
        attributeNames = trimmedNames;
    }
    const isKraftBoxes = parentProduct.productNumber === "KraftBoxes";
    const tableStyles = isKraftBoxes ? styles.kraftBoxesTable : styles.variantTable;

    return (
        <DataTable {...tableStyles}>
            <thead>
                <DataTableRow>
                    <DataTableHeader rowSpan={priceHeaderRowSpan}>Model No.</DataTableHeader>
                    {getVariantHeaders(variantHeaders)}
                    {getAttributeHeaders(attributeNames, variantChildren[0])}
                    {getTopPriceHeader(priceHeaders)}
                    <DataTableHeader rowSpan={priceHeaderRowSpan}>Add to Cart</DataTableHeader>
                </DataTableRow>
                {priceHeaderRowSpan === 2 && (
                    <DataTableRow>{getPriceHeaders(priceHeaders, parentProduct.id)}</DataTableRow>
                )}
            </thead>
            <DataTableBody>
                {variantChildren?.map((child, index) => (
                    <VariantRow
                        product={
                            !props.isParentChildConnector && !props.product.isVariantParent ? props.product : child
                        }
                        parentProduct={parentProduct}
                        variantChild={child}
                        priceHeaders={priceHeaders}
                        isParentChildConnector={props.isParentChildConnector}
                        attributeNames={attributeNames}
                        childrenCount={variantChildren.length}
                        key={`vr${child.id + index}`}
                    ></VariantRow>
                ))}
            </DataTableBody>
        </DataTable>
    );
};
export default VariantTable;
